/**
 * SUBSCRIPTIONS (EXPERT-ROOMS.md §14a, owner 2026-10-27) — an Expert Network's Settings sub-tab: who uses this expert,
 * who is waiting, approve / decline / remove each, and how a workspace gets in (you approve each, or anyone in the
 * company). The network's creator or a workspace admin acts (``network.canChangeAccess``); the server enforces it.
 * Same data + actions as the Inbox item (PUT /networks/{id} ``{companySharing: {action, tenantId?, approval?}}``).
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { confirmDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';
import { formatMessageTime } from '@stepflow/ui-shared/utils/formatDate.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const STATUS = { active: 'Using it', pending: 'Waiting for you', revoked: 'Removed' };
const TONE = { active: 'text-emerald-700', pending: 'text-amber-700', revoked: 'text-gray-400' };

export default function ExpertSubscriptionsTab({ network, onNetworkUpdate }) {
  const cs = network?.companySharing;
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  if (!cs || !cs.available) {
    return <p className="p-4 text-xs text-gray-500" data-testid="expert-subscriptions-none">This workspace isn&apos;t part of a company — there&apos;s no one to share with.</p>;
  }
  const canAct = !!network?.canChangeAccess;
  const subs = [...(cs.subscribers || [])].sort((a, b) => ({ pending: 0, active: 1, revoked: 2 }[a.status] ?? 3)
    - ({ pending: 0, active: 1, revoked: 2 }[b.status] ?? 3));
  const act = async (req, key) => {
    setBusy(key); setErr('');
    try {
      const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(network.networkId)}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ companySharing: req }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
      onNetworkUpdate?.({ ...network, companySharing: body.companySharing || body });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };
  const spin = (key, label) => (busy === key
    ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" aria-hidden />{label}</span> : label);
  const remove = async (s, key) => {
    if (await confirmDialog({ title: `Remove ${s.tenantName || 'this workspace'}?`, danger: true, confirmLabel: 'Remove',
      message: 'Its rooms lose the expert at once — its rules and its answers.' })) act({ action: 'revoke', tenantId: s.tenantId }, key);
  };
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 text-xs" data-testid="expert-subscriptions-tab">
      <h3 className="text-sm font-semibold text-gray-900">Subscriptions</h3>
      <p className="mt-0.5 max-w-prose text-gray-500">
        The workspaces of your company that use this expert. Their rooms follow its rules and ask it questions — only
        the questions come to you, never their rooms or files.
      </p>
      {!cs.published ? (
        <p className="mt-3 text-gray-500" data-testid="expert-subscriptions-unpublished">
          Not shared yet — release it to the registry (⋮ → Releases &amp; stages) and workspaces can ask to use it.
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="expert-subscriptions-approval">
            <span className="text-gray-600">New workspaces:</span>
            {[['manual', 'You approve each'], ['auto', 'Anyone in the company']].map(([id, label]) => (
              <label key={id} className="inline-flex items-center gap-1 text-gray-700">
                <input type="radio" name="expert-sub-approval" checked={(cs.approval || 'manual') === id}
                  disabled={!canAct || !!busy} data-testid={`expert-subscriptions-approval-${id}`}
                  onChange={() => act({ action: 'publish', approval: id }, `approval:${id}`)} />
                {spin(`approval:${id}`, label)}
              </label>
            ))}
          </div>
          <table className="mt-3 w-full" data-testid="expert-subscriptions-list">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] text-gray-500">
                <th className="py-1 font-medium">Workspace</th><th className="py-1 font-medium">Status</th>
                <th className="py-1 font-medium">Since</th><th className="py-1 font-medium">Decided by</th><th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subs.length === 0 ? <tr><td colSpan={5} className="py-2 text-gray-400">No workspace uses it yet.</td></tr> : null}
              {subs.map((s) => (
                <tr key={s.tenantId} data-testid={`expert-sub-${s.tenantId}`}>
                  <td className="py-1.5 font-medium text-gray-800">{s.tenantName || s.tenantId}</td>
                  <td className={`py-1.5 ${TONE[s.status] || 'text-gray-500'}`}>{STATUS[s.status] || s.status}</td>
                  <td className="py-1.5 text-gray-500">{s.at ? formatMessageTime(s.at) : '—'}</td>
                  <td className="py-1.5 text-gray-500">{s.decidedBy ? s.decidedBy : '—'}</td>
                  <td className="py-1.5 text-right">
                    {canAct && s.status === 'pending' ? (
                      <span className="inline-flex gap-1">
                        <button type="button" disabled={!!busy} data-testid={`expert-sub-approve-${s.tenantId}`}
                          onClick={() => act({ action: 'approve', tenantId: s.tenantId }, `approve:${s.tenantId}`)}
                          className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50`}>
                          {spin(`approve:${s.tenantId}`, 'Approve')}
                        </button>
                        <button type="button" disabled={!!busy} data-testid={`expert-sub-decline-${s.tenantId}`}
                          onClick={() => act({ action: 'revoke', tenantId: s.tenantId }, `decline:${s.tenantId}`)}
                          className={`${btn} text-rose-700 hover:bg-rose-50 disabled:opacity-50`}>
                          {spin(`decline:${s.tenantId}`, 'Decline')}
                        </button>
                      </span>
                    ) : canAct && s.status === 'active' ? (
                      <button type="button" disabled={!!busy} data-testid={`expert-sub-remove-${s.tenantId}`}
                        onClick={() => remove(s, `remove:${s.tenantId}`)}
                        className={`${btn} text-rose-700 hover:bg-rose-50 disabled:opacity-50`}>
                        {spin(`remove:${s.tenantId}`, 'Remove')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {!canAct ? <p className="mt-2 text-[11px] text-gray-400">Only its creator or a workspace admin changes these.</p> : null}
      {err ? <p className="mt-2 text-rose-600">{err}</p> : null}
    </div>
  );
}
